import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Blocks access to the app for any authenticated user whose profile.is_locked = true.
 * Parent can lock/unlock from their dashboard.
 */
const AccountLockGate = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setChecked(true);
      setLocked(false);
      return;
    }
    let cancelled = false;

    const check = async () => {
      const { data } = await (supabase as any)
        .from("profiles")
        .select("is_locked")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      setLocked(Boolean(data?.is_locked));
      setChecked(true);
    };
    check();

    // Realtime: react instantly when parent flips the switch
    const channel = supabase
      .channel(`lock-${user.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${user.id}` },
        (payload: any) => {
          setLocked(Boolean(payload.new?.is_locked));
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [user, loading]);

  if (!checked) return <>{children}</>;
  if (!locked) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-[9999] bg-background flex items-center justify-center p-6">
      <div className="card-edu max-w-md w-full p-8 text-center space-y-5">
        <div className="w-20 h-20 rounded-full bg-destructive/15 flex items-center justify-center mx-auto">
          <Lock className="w-10 h-10 text-destructive" />
        </div>
        <div>
          <h1 className="text-xl font-black text-foreground mb-2">Compte verrouillé</h1>
          <p className="text-sm text-muted-foreground font-medium">
            Merci de contacter votre parent pour réactiver l'accès à l'application.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <button
            onClick={async () => {
              await signOut();
              navigate("/", { replace: true });
            }}
            className="btn-hero w-full py-2.5 justify-center text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountLockGate;